// Firebase configuration and initialization
import { initializeApp } from 'firebase/app'
import { getFirestore } from 'firebase/firestore'
import { getAnalytics, isSupported as isAnalyticsSupported, logEvent } from 'firebase/analytics'

const firebaseConfig = {
  apiKey: "AIzaSyCQ5SCL0vAgx6M3XA8cUrT-2_a8vj3JMUg",
  authDomain: "akhtar-sabir-shop.firebaseapp.com",
  projectId: "akhtar-sabir-shop",
  storageBucket: "akhtar-sabir-shop.firebasestorage.app",
  messagingSenderId: "707842530030",
  appId: "1:707842530030:web:2a4ebf6cbef0277e74b1ff",
  measurementId: "G-KL83D13ZRL"
}

const app = initializeApp(firebaseConfig)

// Firestore database instance, used everywhere else in the app
export const db = getFirestore(app)

// Authentication is only ever used behind the admin login/dashboard routes
// (already lazy-loaded per route), but "firebase/auth" was previously a
// static import here, so it still shipped in the main bundle every
// anonymous storefront visitor downloads. Loading it on demand instead -
// see AuthContext, the only consumer - splits it into its own chunk.
let firebaseAuthPromise = null
export const getFirebaseAuth = () => {
  if (!firebaseAuthPromise) {
    firebaseAuthPromise = import('firebase/auth').then((authModule) => ({
      ...authModule,
      auth: authModule.getAuth(app)
    }))
  }
  return firebaseAuthPromise
}

// Analytics - guarded with isSupported() since it relies on IndexedDB/cookies
// and isn't available in every environment (e.g. some in-app browsers,
// private browsing). This is a React SPA, so a page_view event has to be
// logged manually on every route change (see App.jsx) - Analytics only
// captures the very first load automatically otherwise.
export const analyticsReady = isAnalyticsSupported().then((supported) => (
  supported ? getAnalytics(app) : null
))

// isSupported() (and therefore analyticsInstance) resolves asynchronously,
// so a component whose effect fires very early after a fresh page load -
// most exposed case: landing straight on a product page from an ad or a
// shared link - could call one of these before analyticsInstance is set,
// silently dropping the event under the old `if (analyticsInstance)`
// guard. Chaining onto analyticsReady instead means the event always
// waits for that resolution (a no-op if analytics genuinely isn't
// supported in this browser) instead of racing it.
const withAnalytics = (callback) => {
  analyticsReady.then((instance) => {
    if (instance) callback(instance)
  })
}

export const logPageView = (pagePath) => {
  withAnalytics((instance) => logEvent(instance, 'page_view', { page_path: pagePath }))
}

// Fired once when an order is successfully placed - this is the actual
// conversion signal ad platforms need to optimize toward buyers instead of
// just generic traffic. GA4's standard "purchase" event, which Google Ads
// can import as a conversion action once the two accounts are linked.
export const logOrderPlaced = (order) => {
  withAnalytics((instance) => logEvent(instance, 'purchase', {
    transaction_id: order.id,
    value: order.total,
    currency: 'PKR',
    items: order.items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      quantity: item.quantity
    }))
  }))
}

// GA4's standard e-commerce funnel events - same reasoning as the Meta
// Pixel equivalents in metaPixel.js: purchase-only data gives Smart
// Bidding almost nothing to optimize toward if/when Google Ads gets
// linked to this property.
export const logViewItem = (product) => {
  withAnalytics((instance) => logEvent(instance, 'view_item', {
    currency: 'PKR',
    value: product.price,
    items: [{ item_id: product.id, item_name: product.name, price: product.price }]
  }))
}

export const logAddToCart = (product, quantity, effectivePrice) => {
  withAnalytics((instance) => logEvent(instance, 'add_to_cart', {
    currency: 'PKR',
    value: effectivePrice * quantity,
    items: [{ item_id: product.id, item_name: product.name, price: effectivePrice, quantity }]
  }))
}

export const logBeginCheckout = (cart, cartTotal) => {
  withAnalytics((instance) => logEvent(instance, 'begin_checkout', {
    currency: 'PKR',
    value: cartTotal,
    items: cart.map((item) => ({ item_id: item.id, item_name: item.name, price: item.price, quantity: item.quantity }))
  }))
}

// The click stage of the WhatsApp/Phone funnels - see the matching
// trackPixelContact() in metaPixel.js and the note in attribution.js on
// why this is as far as any website-side tracking can reach: whatever
// happens after the click (a conversation, a call, an order) happens off
// this site entirely. `method` is 'whatsapp' or 'phone'; `page` is where
// the click happened, useful for seeing which pages actually drive contact
// intent in GA4's event reports.
export const logContactClick = (method, page) => {
  withAnalytics((instance) => logEvent(instance, 'contact_click', { method, page }))
}